import { queryClient } from "@/api/utils/useQuery"
import { BackofficeRestMutations } from "@/api/backofficeRest/mutations"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import { isBackofficeApiError } from "@/api/backofficeRest/backofficeApiError"
import errorHandler from "@/services/ErrorService/errorHandler"
import errorReporter from "@/services/ErrorService/errorReporter"
import {
  IMarketingBannerPatch,
  IMarketingBannerWrite,
  IMarketingBannerWriteFailure,
  IMarketingBannerWriteResult,
} from "./AlertsBannersMutator.types"

const FALLBACK_MESSAGE = "Could not save the banner"

const toFailure = (
  id: number,
  error: unknown,
): IMarketingBannerWriteFailure => {
  if (!isBackofficeApiError(error)) {
    errorReporter.report(error)
    return { id, message: FALLBACK_MESSAGE, fields: [] }
  }
  errorHandler.reportApiError(error)
  return {
    id,
    message: errorHandler.userMessage(error, FALLBACK_MESSAGE),
    fields: error.fields.flatMap((f) => (f.name ? [f.name] : [])),
  }
}

class AlertsBannersMutatorClass {
  updateRow(
    id: number,
    patch: IMarketingBannerPatch,
  ): Promise<IMarketingBannerWriteResult> {
    return this.updateBanners([{ rowIds: [id], patch }])
  }

  async updateBanners(
    writes: IMarketingBannerWrite[],
  ): Promise<IMarketingBannerWriteResult> {
    const { mutationFn } =
      BackofficeRestMutations.alertsBanners.updateMarketingBanner()
    const rows = writes.flatMap(({ rowIds, patch }) =>
      rowIds.map((id) => ({ id, patch })),
    )
    const outcomes = await Promise.allSettled(
      rows.map(({ id, patch }) => mutationFn({ id, body: patch })),
    )
    const result: IMarketingBannerWriteResult = { succeeded: [], failed: [] }
    outcomes.forEach((outcome, index) => {
      const { id } = rows[index]
      if (outcome.status === "fulfilled") result.succeeded.push(id)
      else result.failed.push(toFailure(id, outcome.reason))
    })
    // Also after a partial failure, so the page shows what actually landed.
    await queryClient.invalidateQueries({
      queryKey: BackofficeRestQueries.alertsBanners.definitions.queryKey,
    })
    return result
  }
}

export const AlertsBannersMutator = new AlertsBannersMutatorClass()
